import type { CSSProperties, ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  placement?: "right" | "left" | "bottom";
  width?: number;
  height?: number;
  footer?: ReactNode;
  children: ReactNode;
}

const SIDE: Record<NonNullable<DrawerProps["placement"]>, string> = {
  right:
    "inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  left: "inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
  bottom:
    "inset-x-0 bottom-0 w-full border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
};

export function Drawer({
  open,
  onClose,
  title,
  placement = "right",
  width = 360,
  height,
  footer,
  children,
}: DrawerProps) {
  const sizeStyle: CSSProperties =
    placement === "bottom" ? { height: height ?? "60vh", maxHeight: "90vh" } : { width, maxWidth: "100vw" };
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1100] bg-black/50 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          style={sizeStyle}
          className={cn(
            "fixed z-[1100] flex flex-col bg-card text-card-foreground shadow-lg transition data-[state=closed]:animate-out data-[state=open]:animate-in",
            SIDE[placement],
          )}
        >
          <div className="flex items-center justify-between gap-4 border-b px-5 py-4">
            {title != null ? (
              <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
            ) : (
              <Dialog.Title className="sr-only">Drawer</Dialog.Title>
            )}
            <Dialog.Close className="rounded-sm text-muted-foreground opacity-70 transition hover:opacity-100">
              <X className="size-4" />
              <span className="sr-only">Kapat</span>
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer != null && <div className="border-t px-5 py-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
